import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

import { API, hasBackend, CONTACT_EMAIL, CONTACT_PHONE } from "@/lib/api";

const OFFLINE_REPLY =
  `Der KI-Berater ist in dieser Umgebung gerade nicht verbunden. ` +
  `Ihre Frage beantworten wir aber gerne persönlich: ${CONTACT_PHONE} ` +
  `oder ${CONTACT_EMAIL}. Konkrete Zahlen liefert Ihnen sofort der Solarrechner weiter oben.`;

function generateSessionId() {
  if (typeof window === "undefined") return "anon";
  let id = window.localStorage.getItem("sw_chat_session");
  if (!id) {
    id = "sw_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    window.localStorage.setItem("sw_chat_session", id);
  }
  return id;
}

const GREETING = {
  role: "assistant",
  content:
    "Hallo! Ich bin Helios, Ihr KI-Berater von Sonnenwerk. Ich helfe Ihnen gerne weiter – ob zu Photovoltaik, Speicher, Wallbox oder Förderung. Was möchten Sie wissen?",
};

const SUGGESTIONS = [
  "Was kostet eine Anlage für ein EFH?",
  "Wie funktioniert die Pronovo-Förderung?",
  "Lohnt sich ein Speicher?",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async (text) => {
    const userText = (text ?? input).trim();
    if (!userText || streaming) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: userText },
      { role: "assistant", content: "" },
    ]);

    // Ohne Backend gar nicht erst ins Leere laufen, sondern ehrlich weiterhelfen.
    if (!hasBackend) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: OFFLINE_REPLY };
        return copy;
      });
      return;
    }

    setStreaming(true);

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: userText }),
        signal: controller.signal,
      });
      if (!res.body) throw new Error("No stream body");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE parsing - split by double newline
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.startsWith("data: ") ? part.slice(6) : part;
          if (line === "[DONE]") continue;
          setMessages((prev) => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, content: last.content + line };
            }
            return copy;
          });
        }
      }
    } catch (e) {
      console.error("chat error", e);
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Entschuldigung, ich konnte gerade nicht antworten. Bitte versuchen Sie es erneut oder nutzen Sie das Kontaktformular.",
          };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#1A1D20] text-[#EAB308] shadow-[0_12px_36px_-8px_rgba(20,20,30,0.5)] flex items-center justify-center border-2 border-[#EAB308]/70"
        aria-label="KI-Chat öffnen"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}>
              <X size={22} />
            </motion.span>
          ) : (
            <motion.span key="msg" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}>
              <MessageSquare size={22} />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#EAB308] glow-anim" />
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-panel"
            role="dialog"
            aria-label="KI-Berater Helios"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] max-h-[78vh] bg-white rounded-2xl border border-black/10 shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#1A1D20] text-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#EAB308] text-[#1A1D20] flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold">Helios</div>
                <div className="text-xs text-white/60 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Online · KI-Berater
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chatbot-close"
                className="text-white/60 hover:text-white"
                aria-label="Schliessen"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="chat-scroll flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF7EF]"
              data-testid="chatbot-messages"
              role="log"
              aria-live="polite"
              aria-label="Chatverlauf"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#1A1D20] text-white rounded-br-md"
                        : "bg-white border border-black/5 text-[#1A1D20] rounded-bl-md"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1A1D20]/40 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1A1D20]/40 animate-bounce [animation-delay:120ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1A1D20]/40 animate-bounce [animation-delay:240ms]" />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length === 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`chatbot-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EAB308]/40 text-[#1A1D20] hover:bg-[#EAB308]/10 transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-black/5 p-3 bg-white flex items-center gap-2"
            >
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Frage stellen…"
                disabled={streaming}
                className="flex-1 h-11 px-4 rounded-full bg-[#FAF7EF] border border-black/5 text-sm focus:outline-none focus:border-[#EAB308] disabled:opacity-60"
              />
              <button
                type="submit"
                data-testid="chatbot-send"
                disabled={streaming || !input.trim()}
                className="w-11 h-11 rounded-full bg-[#EAB308] text-[#1A1D20] flex items-center justify-center hover:bg-[#CA9A06] disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Senden"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
